export const timeToMinutes = (timeString) => {
    if (!timeString || typeof timeString !== "string") {
        return 0;
    }
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
};

export const prepareStaffRequirements = (rawRequirements) => {
    const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    return daysOfWeek.map((day, index) => ({
        dayOfWeek: day,
        maxStaff: parseInt(rawRequirements.max[index]),
        minStaff: parseInt(rawRequirements.min[index])
    }));
};

export const prepareStaffDistributions = (rawDistributions) => {
    return Object.entries(rawDistributions).map(([day, intervals]) => ({
        dayOfWeek: day,
        timeIntervals: intervals.map(interval => ({
            startTime: Number(interval.startTime),
            endTime: Number(interval.endTime),
            staffCount: Number(interval.staff)
        }))
    }));
};

const formatDecimal = (value) => {
    if (typeof value === 'string') {
        return value.replace(',', '.');
    }
    return value;
};

export const prepareSchedules = (rawSchedules) => {
    return rawSchedules.map(schedule => {
        let startTime = schedule.startTime;
        let endTime = schedule.endTime;

        if (startTime === null || endTime === null) {
            const times = schedule.legend.split('-');
            if (times.length >= 2) {
                const startHour = parseInt(times[0]);
                startTime = startHour * 60;

                const endPart = times[1].replace(',', '.');
                const [endHour, endDecimal] = endPart.split('.');
                endTime = parseInt(endHour) * 60;
                if (endDecimal) {
                    endTime += Math.floor(parseInt(endDecimal) / 10 * 60);
                }
            }
        }

        return {
            ...schedule,
            code: schedule.code.replace(',', '.'),
            weightTime: formatDecimal(schedule.weightTime),
            startTime: timeToMinutes(startTime) || 0,  
            endTime: timeToMinutes(endTime) || 0,      
            mealBreaks: schedule.mealBreaks.map(mealBreak => ({
                ...mealBreak,
                startTime: mealBreak.startTime ? Number(mealBreak.startTime) : null,
                endTime: mealBreak.endTime ? Number(mealBreak.endTime) : null
            }))
        };
    });
};



const prepareShiftSequences = (sequences) => {
   return sequences.map(seq => ({
        ...seq,
        shift: seq.shift.replace(',', '.'),
        hard_min: Number(seq.hard_min),
        soft_min: Number(seq.soft_min),
        min_penalty: Number(seq.min_penalty),
        soft_max: Number(seq.soft_max),
        hard_max: Number(seq.hard_max),
        max_penalty: Number(seq.max_penalty)
    }));
};

const prepareWeeklySums = (sums) => {
    return sums.map(sum => ({
        ...sum,
        shift: sum.shift.replace(',', '.'),
        hard_min: Number(sum.hard_min),
        soft_min: Number(sum.soft_min),
        min_penalty: Number(sum.min_penalty),
        soft_max: Number(sum.soft_max),
        hard_max: Number(sum.hard_max),
        max_penalty: Number(sum.max_penalty)
    }));
};

export const prepareData = () => {
    const rawRequirements = JSON.parse(sessionStorage.getItem('staffRequirements') || '{}');
    const rawDistributions = JSON.parse(sessionStorage.getItem('distributions') || '{}');
    const rawSchedules = JSON.parse(sessionStorage.getItem('schedules') || '[]');
    const rawShiftSequences = JSON.parse(sessionStorage.getItem('shiftSequenceParams') || '[]');
    const rawWeeklySums = JSON.parse(sessionStorage.getItem('weeklySumParams') || '[]');
    const employees = JSON.parse(sessionStorage.getItem('employees') || '[]');
    const generalInfo = JSON.parse(sessionStorage.getItem('general') || '{}');
    const shiftRestrictions = JSON.parse(sessionStorage.getItem('shiftRestrictions') || '{}');
    const absenceEntries = JSON.parse(sessionStorage.getItem('absenceEntries') || '[]');
    const legislation = JSON.parse(sessionStorage.getItem("legislation") || '{}');
    const penalizedTransitions = JSON.parse(sessionStorage.getItem('penalizedTransitions') || '[]');
    const penalizedTransitions3 = JSON.parse(sessionStorage.getItem('penalizedTransitions_3') || '[]');
    const penalizedTransitions4 = JSON.parse(sessionStorage.getItem('penalizedTransitions_4') || '[]');
    const penalizedTransitions5 = JSON.parse(sessionStorage.getItem('penalizedTransitions_5') || '[]');
  
    const processedEmployees = employees.map(emp => ({
      ...emp,
      wkl_wrk_load: Number(emp.wkl_wrk_load),
      day_WI: Number(emp.day_WI),
      at_roster_num: Number(emp.at_roster_num),
      number: Number(emp.number),
      team: Number(emp.team)
    }));
  
    const empOnlySch = [];
    const empForbidSch = [];
  
    Object.entries(shiftRestrictions).forEach(([employeeId, restrictions]) => {
      const employeeObj = processedEmployees.find(emp => emp.number.toString() === employeeId);
      if (!employeeObj) return;
      
      const employeeIndex = processedEmployees.indexOf(employeeObj);
      
      restrictions.forEach(restriction => {
        const scheduleIndex = rawSchedules.findIndex(sch => sch.code === restriction.scheduleCode);
        if (scheduleIndex === -1) return; 
        
        if (restriction.allowOnly) {
          empOnlySch.push([employeeIndex, scheduleIndex]);
        } else {
          empForbidSch.push([employeeIndex, scheduleIndex]);
        }
      });
    });
  
    return {
      employees: processedEmployees,
      generalInfo: {
        ...generalInfo,
        weeks: Number(generalInfo.weeks),
        days: Number(generalInfo.days),
        cycle: Number(generalInfo.cycle),
        offset: Number(generalInfo.offset),
        GA: Number(generalInfo.GA),
        OFFsum: Number(generalInfo.OFFsum),
        week_or_days: Number(generalInfo.week_or_days)
      },
      schedules: prepareSchedules(rawSchedules),
      staffRequirements: prepareStaffRequirements(rawRequirements),
      staffDistributions: prepareStaffDistributions(rawDistributions),
      shiftSequences: prepareShiftSequences(rawShiftSequences),
      weeklySums: prepareWeeklySums(rawWeeklySums),
      empOnlySch: empOnlySch,
      empForbidSch: empForbidSch,
      absenceEntries: absenceEntries,
      legislation: legislation,
      penalizedTransitions: penalizedTransitions,
      penalizedTransitions3: penalizedTransitions3,
      penalizedTransitions4: penalizedTransitions4,
      penalizedTransitions5: penalizedTransitions5,
    };
  };